// Two-Track §M2 · Discovery summary renderer (shared between
// Command Center owner surface + public /discover/{id} claim page).
// Tenant-neutral. Renders whatever the /genesis/runs/{id}/summary or
// /genesis/public/discover/{id}/summary endpoints return.
import React from "react";

function pretty(value) {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.filter(Boolean).map(String).join(", ");
  if (typeof value === "object") {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

function confidenceBadge(conf) {
  const norm = String(conf || "").toLowerCase();
  const color = norm === "high" ? "#3f6d3f" : norm === "medium" ? "#8a6a1f" : "#6b6558";
  return (
    <span
      style={{
        display: "inline-block",
        fontFamily: "var(--bo-mono, monospace)",
        fontSize: 10,
        letterSpacing: 1.4,
        textTransform: "uppercase",
        color: "#fff",
        background: color,
        padding: "2px 7px",
        borderRadius: 6,
        marginLeft: 8,
        verticalAlign: "middle",
      }}
    >
      {norm || "unknown"}
    </span>
  );
}

function Section({ title, entries, testIdPrefix, emptyLabel }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="bo-card" data-testid={`${testIdPrefix}-empty`} style={{ marginBottom: 16 }}>
        <div className="bo-eyebrow">{title}</div>
        <p style={{ color: "var(--bo-muted)", fontSize: 13, margin: "6px 0 0" }}>
          {emptyLabel || "No proposals yet. If the discovery is still running, refresh in a few seconds."}
        </p>
      </div>
    );
  }
  return (
    <div className="bo-card" data-testid={testIdPrefix} style={{ marginBottom: 16 }}>
      <div className="bo-eyebrow">{title}</div>
      <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0" }}>
        {entries.map((e, i) => (
          <li
            key={`${e.proposal_id || e.field_path}-${i}`}
            data-testid={`${testIdPrefix}-item-${i}`}
            style={{
              padding: "10px 0",
              borderTop: i === 0 ? "none" : "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <div style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono, monospace)" }}>
              {e.field_path}
              {confidenceBadge(e.confidence)}
            </div>
            <div style={{ fontSize: 14, marginTop: 4, color: "var(--bo-ink)" }}>
              {pretty(e.value)}
            </div>
            {e.source_url ? (
              <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 4 }}>
                <a href={e.source_url} target="_blank" rel="noreferrer" data-testid={`${testIdPrefix}-source-${i}`}>{e.source_url}</a>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DiscoverySummary({ summary, testIdPrefix = "discovery-summary" }) {
  if (!summary) return null;
  const counts = summary.counts || {};
  const insights = summary.instant_insights || null;
  const running = summary.status && summary.status !== "succeeded" && summary.status !== "failed" && summary.status !== "complete";
  const trustColor = insights?.trust_signal === "high" ? "#3f6d3f"
    : insights?.trust_signal === "mixed" ? "#8a6a1f"
    : insights?.trust_signal === "low" ? "#b34a4a"
    : "#6b6558";
  return (
    <div data-testid={testIdPrefix}>
      <div className="bo-card" style={{ marginBottom: 16 }} data-testid={`${testIdPrefix}-scorecard`}>
        <div className="bo-eyebrow">Discovery snapshot</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginTop: 10 }}>
          <Stat label="Status" value={summary.status || "unknown"} testId={`${testIdPrefix}-status`} />
          <Stat label="Proposals" value={counts.proposals_total ?? "0"} testId={`${testIdPrefix}-proposals-count`} />
          <Stat label="Sources" value={(counts.sources_created ?? 0) + (counts.sources_updated ?? 0)} testId={`${testIdPrefix}-sources-count`} />
          <Stat label="Chunks" value={counts.chunks_total ?? "0"} testId={`${testIdPrefix}-chunks-count`} />
        </div>
        {running ? (
          <p style={{ fontSize: 12, color: "var(--bo-muted)", margin: "10px 0 0" }}>
            Still working — Genesis is crawling, classifying, and extracting from the URL. Refresh in ~30–60 seconds for the full picture.
          </p>
        ) : null}
        {summary.error ? (
          <p style={{ fontSize: 12, color: "#b34a4a", margin: "10px 0 0" }} data-testid={`${testIdPrefix}-error`}>
            {typeof summary.error === "string" ? summary.error : JSON.stringify(summary.error)}
          </p>
        ) : null}
      </div>

      {insights && (insights.extracted_count > 0 || insights.inferred_count > 0) ? (
        <div className="bo-card" style={{ marginBottom: 16, borderColor: trustColor + "55" }} data-testid={`${testIdPrefix}-instant-insights`}>
          <div className="bo-eyebrow" style={{ color: trustColor }}>Instant insights · extracted vs inferred</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 10 }}>
            <div data-testid={`${testIdPrefix}-extracted-block`}>
              <div style={{ fontSize: 10, color: "var(--bo-muted)", textTransform: "uppercase", letterSpacing: 1.4 }}>
                In your own words
              </div>
              <div style={{ fontFamily: "var(--bo-serif)", fontSize: 30, color: "#3f6d3f", marginTop: 2 }}>
                {insights.extracted_count}
              </div>
              <p style={{ fontSize: 12, color: "var(--bo-muted)", margin: "4px 0 0" }}>
                Claim(s) extracted verbatim from a page on your site — highest trust.
              </p>
            </div>
            <div data-testid={`${testIdPrefix}-inferred-block`}>
              <div style={{ fontSize: 10, color: "var(--bo-muted)", textTransform: "uppercase", letterSpacing: 1.4 }}>
                Genesis inferred
              </div>
              <div style={{ fontFamily: "var(--bo-serif)", fontSize: 30, color: "#8a6a1f", marginTop: 2 }}>
                {insights.inferred_count}
              </div>
              <p style={{ fontSize: 12, color: "var(--bo-muted)", margin: "4px 0 0" }}>
                Claim(s) inferred from context. Review carefully before promoting to approved truth.
              </p>
            </div>
          </div>
          {insights.extracted_pct !== null && insights.extracted_pct !== undefined ? (
            <div style={{ marginTop: 12 }} data-testid={`${testIdPrefix}-trust-bar`}>
              <div style={{ height: 6, background: "rgba(0,0,0,0.06)", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.round(insights.extracted_pct * 100)}%`, background: "#3f6d3f", transition: "width .4s ease" }} />
              </div>
              <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 4, fontFamily: "var(--bo-mono, monospace)" }}>
                {Math.round(insights.extracted_pct * 100)}% extracted · {Math.round((1 - insights.extracted_pct) * 100)}% inferred
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <Section title="Brand identity" entries={summary.identity} testIdPrefix={`${testIdPrefix}-identity`} />
      <Section title="Positioning" entries={summary.positioning} testIdPrefix={`${testIdPrefix}-positioning`} />
      <Section title="Voice" entries={summary.voice} testIdPrefix={`${testIdPrefix}-voice`} />
      <Section title="Products" entries={summary.products} testIdPrefix={`${testIdPrefix}-products`} emptyLabel="No product-specific claims yet." />
      <Section title="Audiences" entries={summary.audiences} testIdPrefix={`${testIdPrefix}-audiences`} emptyLabel="No audience-specific findings yet." />
      {summary.warnings && summary.warnings.length > 0 ? (
        <div className="bo-card" style={{ marginBottom: 16, borderColor: "rgba(179, 74, 74, 0.4)" }} data-testid={`${testIdPrefix}-warnings`}>
          <div className="bo-eyebrow" style={{ color: "#b34a4a" }}>Warnings</div>
          <p style={{ fontSize: 12, color: "var(--bo-muted)", margin: "6px 0" }}>
            {summary.warnings.length} high-confidence claim(s) lack a citable source. Owner review required before promotion to approved truth.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div style={{ fontSize: 10, color: "var(--bo-muted)", textTransform: "uppercase", letterSpacing: 1.4 }}>
        {label}
      </div>
      <div style={{ fontFamily: "var(--bo-serif)", fontSize: 22, color: "var(--bo-ink)", marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}
